"use client";

import React, { useState } from "react";
import { Eye } from "lucide-react";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
interface EcommerceProduct {
  id: number;
  title: string;
  slug: string;
  short_description: string;
  long_description: string;
  price: number;
  featured_image_url: string;
  discounted_price?: number;
  categories: Array<{
    id: number;
    name: string;
    slug: string;
    link: string;
  }>;
  tags: Array<{
    id: number;
    name: string;
    slug: string;
    link: string;
  }>;
  link: string;
  customFields?: Record<string, any>;
}
function ProductShowcaseConsumer() {
  const {
    products,
    loading,
    error,
    refetch
  } = useEcommerceProducts();
  const [quickViewProduct, setQuickViewProduct] = useState<EcommerceProduct | null>(null);
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="h-[400px] rounded-xl bg-muted/30 animate-pulse" />)}
      </div>;
  }
  if (error) {
    return <div className="text-center py-12">
        <p className="text-muted-foreground mb-4">Unable to load the collection at this time.</p>
        <Button onClick={() => refetch()} variant="outline">Try Again</Button>
      </div>;
  }
  if (products.length === 0) {
    return <p className="text-center text-muted-foreground py-12">Our curators are currently updating the collection.</p>;
  }
  return <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {products.map((product, i) => <div key={product.id} data-index={i} className="group relative">
            <Card className="h-full border-none shadow-sm hover:shadow-md transition-shadow duration-300 bg-neutral-800 overflow-hidden flex flex-col">
              <div className="relative aspect-[4/5] overflow-hidden bg-muted/20">
                {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" /> : <div className="h-full w-full flex items-center justify-center bg-muted text-muted-foreground">
                    No Image Available
                  </div>}
                
                {}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/20 backdrop-blur-[2px]">
                  <Button onClick={() => setQuickViewProduct(product as unknown as EcommerceProduct)} variant="secondary" size="lg" className="gap-2 font-medium shadow-lg translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                    <Eye className="w-4 h-4" />
                    Quick View
                  </Button>
                </div>
              </div>

              <CardContent className="pt-6 flex-grow text-center">
                <h3 className="font-serif italic text-xl font-medium text-zinc-100 mb-2 line-clamp-2">
                  {product.title}
                </h3>
                <div className="font-sans font-bold text-lg text-secondary">
                  {product.discounted_price ? <div className="flex items-center justify-center gap-2">
                      <span className="line-through text-zinc-400 text-sm font-normal">
                        ${product.price.toLocaleString()}
                      </span>
                      <span>${product.discounted_price.toLocaleString()}</span>
                    </div> : <span>${product.price?.toLocaleString() ?? "Price on Request"}</span>}
                </div>
              </CardContent>

              <CardFooter className="pb-6 pt-0 justify-center">
                <Button asChild variant="outline" className="w-full border-secondary/40 hover:border-secondary hover:bg-neutral-950 text-secondary">
                  <Link to={product.link}>View Provenance</Link>
                </Button>
              </CardFooter>
            </Card>
          </div>)}
      </div>

      {}
      <Dialog open={!!quickViewProduct} onOpenChange={open => !open && setQuickViewProduct(null)}>
        <DialogContent className="sm:max-w-2xl p-0 overflow-hidden gap-0">
          {quickViewProduct && <div className="flex flex-col md:flex-row h-full max-h-[80vh] md:max-h-[600px]">
              <div className="w-full md:w-1/2 bg-muted/20 relative min-h-[300px]">
                {quickViewProduct.featured_image_url && <img src={quickViewProduct.featured_image_url} alt={quickViewProduct.title} className="absolute inset-0 w-full h-full object-cover" />}
              </div>
              <div className="w-full md:w-1/2 p-6 md:p-8 flex flex-col overflow-y-auto">
                <DialogHeader className="text-left mb-4">
                  <DialogTitle className="font-serif text-2xl md:text-3xl mb-2">
                    {quickViewProduct.title}
                  </DialogTitle>
                  <div className="font-sans font-bold text-xl text-primary">
                    ${(quickViewProduct.discounted_price || quickViewProduct.price)?.toLocaleString() ?? "N/A"}
                  </div>
                </DialogHeader>
                
                <div className="prose prose-sm text-muted-foreground mb-6 flex-grow">
                  <div dangerouslySetInnerHTML={{
                __html: quickViewProduct.short_description || quickViewProduct.long_description || "<p>No description available.</p>"
              }} />
                </div>

                <div className="mt-auto space-y-4 pt-4 border-t border-border">
                  <AddToCartButton productId={quickViewProduct.id} variant="default" className="w-full" buttonClassName="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-11 text-base" text="Acquire This Piece" showQuantity={true} />
                  <Button asChild variant="ghost" className="w-full text-muted-foreground hover:text-foreground">
                    <Link to={quickViewProduct.link}>View Full Details</Link>
                  </Button>
                </div>
              </div>
            </div>}
        </DialogContent>
      </Dialog>
    </>;
}
export default function TimelessTreasures() {
  const wp_query_params = {
    per_page: 3,
    orderby: 'date',
    order: 'desc',
    status: 'publish'
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="timeless-treasures" className="py-24 bg-background">
      <div className="max-w-7xl mx-auto px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-foreground mb-6">
            Timeless Treasures
          </h2>
          <p className="font-sans text-lg text-muted-foreground leading-relaxed">
            Explore our latest acquisitions. Each piece in this curated selection represents a unique chapter of history, authenticated by our experts and preserved for the discerning collector.
          </p>
        </div>

        <EcommerceProductsProvider wp_query={{
        per_page: 3,
        orderby: "date",
        order: "desc",
        status: ["publish"]
      }} className="w-full" postType="product">
          <ProductShowcaseConsumer />
        </EcommerceProductsProvider>

        <div className="mt-12 text-center">
          <Button asChild variant="link" className="text-primary text-lg font-medium hover:text-primary/80">
            <Link to="/TheVaultPage">View Entire Collection &rarr;</Link>
          </Button>
        </div>
      </div>
    </section>;
}